/**
 * Role-Based Access Control Utilities
 * Defines what each user role can access in the application
 */

import { ROLE_NAMES } from '@/lib/constants/roles';
import { ROLE_LEVELS } from '@/lib/constants/roles';
export { ROLE_LEVELS };
import type { RoleName } from '@/lib/constants/roles';

// Navigation items by role
export interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  description: string;
  badge?: string | number;
  requiredRoles?: RoleName[];
  requiredPermissions?: string[];
}

// Base navigation - available to all authenticated users
export const BASE_NAVIGATION: NavigationItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: 'Home',
    description: 'Overview and recent activity'
  },
  {
    name: 'Streams',
    href: '/streams',
    icon: 'Play',
    description: 'Browse and watch live streams'
  },
  {
    name: 'Media',
    href: '/media',
    icon: 'Video',
    description: 'Sermons and teachings'
  }
];

// Role-specific navigation items
export const ROLE_SPECIFIC_NAVIGATION: Record<string, NavigationItem[]> = {
  [ROLE_NAMES.MEMBER]: [
    {
      name: 'Comments',
      href: '/comments',
      icon: 'MessageCircle',
      description: 'Your comment history'
    },
    {
      name: 'Notifications',
      href: '/notifications',
      icon: 'Bell',
      description: 'Alerts and updates',
      badge: 'unread'
    }
  ],

  [ROLE_NAMES.PASTOR]: [
    // Inherits MEMBER navigation - dashboard now provides unified functionality
  ],

  [ROLE_NAMES.MEDIA_TEAM]: [
    // Can manage streams
    {
      name: 'Create Stream',
      href: '/admin/streams/new',
      icon: 'Plus',
      description: 'Start a new live stream'
    },
    {
      name: 'Manage Streams',
      href: '/admin/streams',
      icon: 'Play',
      description: 'Manage existing streams'
    }
  ],

  [ROLE_NAMES.MODERATOR]: [
    // Moderation functionality now in unified dashboard
  ],

  [ROLE_NAMES.ADMIN]: [
    // Admin functionality now in unified dashboard
    {
      name: 'User Management',
      href: '/admin/users',
      icon: 'Users',
      description: 'Manage user accounts and roles'
    },
    {
      name: 'Audit Logs',
      href: '/admin/audit-logs',
      icon: 'FileText',
      description: 'View system audit logs'
    },
    {
      name: 'Live Inputs',
      href: '/admin/live-inputs',
      icon: 'Radio',
      description: 'Manage live streaming inputs'
    },
    {
      name: 'Recordings',
      href: '/admin/recordings',
      icon: 'Video',
      description: 'Manage recorded streams'
    },
    {
      name: 'Access Requests',
      href: '/admin/requests',
      icon: 'UserCheck',
      description: 'Review stream access requests'
    },
    {
      name: 'Webhooks',
      href: '/admin/webhooks',
      icon: 'Webhook',
      description: 'Manage webhook integrations'
    },
    {
      name: 'Hero Manager',
      href: '/admin/hero-manager',
      icon: 'Image',
      description: 'Manage landing page hero images'
    }
  ]
};

// User account items - available to all users
export const USER_ACCOUNT_ITEMS: NavigationItem[] = [
  {
    name: 'Profile',
    href: '/profile',
    icon: 'User',
    description: 'Manage your account'
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: 'Settings',
    description: 'App preferences'
  }
];

// Page access control - what pages each role can access
export const PAGE_ACCESS: Record<string, RoleName[]> = {
  // Public/authenticated pages
  '/dashboard': [ROLE_NAMES.MEMBER, ROLE_NAMES.PASTOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
  '/streams': [ROLE_NAMES.MEMBER, ROLE_NAMES.PASTOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
  '/streams/[streamId]': [ROLE_NAMES.MEMBER, ROLE_NAMES.PASTOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
  '/comments': [ROLE_NAMES.MEMBER, ROLE_NAMES.PASTOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
  '/notifications': [ROLE_NAMES.MEMBER, ROLE_NAMES.PASTOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
  '/profile': [ROLE_NAMES.MEMBER, ROLE_NAMES.PASTOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
  '/settings': [ROLE_NAMES.MEMBER, ROLE_NAMES.PASTOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
  // Legacy dashboard routes - now redirect to unified /dashboard
  '/moderator': [ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN], // Redirects to /dashboard
  '/pastor': [ROLE_NAMES.PASTOR, ROLE_NAMES.ADMIN], // Redirects to /dashboard
  '/admin': [ROLE_NAMES.ADMIN], // Redirects to /dashboard
  '/admin/users': [ROLE_NAMES.ADMIN],
  '/admin/users/[id]': [ROLE_NAMES.ADMIN],
  '/admin/audit-logs': [ROLE_NAMES.ADMIN],
  '/admin/live-inputs': [ROLE_NAMES.ADMIN],
  '/admin/recordings': [ROLE_NAMES.ADMIN],
  '/admin/requests': [ROLE_NAMES.ADMIN],
  '/admin/access-requests': [ROLE_NAMES.ADMIN],
  '/admin/webhooks': [ROLE_NAMES.ADMIN],
  '/admin/streams': [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  '/admin/streams/new': [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  '/admin/streams/[id]': [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  '/admin/moderation': [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR],
  '/admin/hero-manager': [ROLE_NAMES.ADMIN],
  '/moderator/reports': [ROLE_NAMES.MODERATOR, ROLE_NAMES.ADMIN],
};

// Component/feature access control
export const FEATURE_ACCESS: Record<string, RoleName[]> = {
  // Stream management
  'create_stream': [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  'manage_streams': [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  'delete_streams': [ROLE_NAMES.ADMIN],

  // Content moderation
  'moderate_comments': [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.PASTOR],
  'moderate_users': [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR],
  'view_reports': [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR],

  // User management
  'manage_users': [ROLE_NAMES.ADMIN],
  'view_user_details': [ROLE_NAMES.ADMIN, ROLE_NAMES.MODERATOR],
  'change_user_roles': [ROLE_NAMES.ADMIN],

  // Analytics and monitoring
  'view_analytics': [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM],
  'view_audit_logs': [ROLE_NAMES.ADMIN],
  'system_admin': [ROLE_NAMES.ADMIN],
};

// Export types and constants for use in components
export { ROLE_NAMES };
export type { RoleName };

// Utility functions
function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0].split('#')[0];
  if (!path) return '/';
  return path !== '/' ? path.replace(/\/+$/, '') : '/';
}

function matchesRoutePattern(pattern: string, pathname: string): boolean {
  const patternParts = normalizePath(pattern).split('/').filter(Boolean);
  const pathParts = normalizePath(pathname).split('/').filter(Boolean);

  if (patternParts.length !== pathParts.length) {
    return false;
  }

  return patternParts.every((segment, index) => {
    // Match dynamic segments like [id], [streamId], etc.
    if (segment.startsWith('[') && segment.endsWith(']')) {
      return pathParts[index].length > 0;
    }
    return segment === pathParts[index];
  });
}

export function canAccessPage(pathname: string, userRole?: RoleName): boolean {
  const normalizedPath = normalizePath(pathname);

  const exactAllowedRoles = PAGE_ACCESS[normalizedPath];
  if (exactAllowedRoles) {
    if (!userRole) return false;
    return exactAllowedRoles.includes(userRole);
  }

  const matchedPattern = Object.keys(PAGE_ACCESS).find((pattern) =>
    pattern.includes('[') && matchesRoutePattern(pattern, normalizedPath)
  );

  if (matchedPattern) {
    const allowedRoles = PAGE_ACCESS[matchedPattern];
    if (!userRole) return false;
    return allowedRoles.includes(userRole);
  }

  // Deny unknown sensitive namespaces by default.
  if (
    normalizedPath.startsWith('/admin') ||
    normalizedPath.startsWith('/moderator') ||
    normalizedPath.startsWith('/pastor')
  ) {
    return false;
  }

  // If not specified, allow access.
  return true;
}

export function canAccessFeature(feature: string, userRole?: RoleName): boolean {
  const allowedRoles = FEATURE_ACCESS[feature];
  if (!allowedRoles) return true; // If not specified, allow access
  if (!userRole) return false; // No role = no access

  return allowedRoles.includes(userRole);
}

export function getAllowedNavigation(userRole?: RoleName): NavigationItem[] {
  if (!userRole) return BASE_NAVIGATION;

  // Start with base navigation
  let navigation = [...BASE_NAVIGATION];

  // Add role-specific navigation
  const roleNavigation = ROLE_SPECIFIC_NAVIGATION[userRole] || [];
  navigation = [...navigation, ...roleNavigation];

  // Add inherited navigation from lower roles
  const userRoleLevel = ROLE_LEVELS[userRole] || 0;
  for (const [roleName, items] of Object.entries(ROLE_SPECIFIC_NAVIGATION)) {
    const roleLevel = ROLE_LEVELS[roleName] || 0;
    if (roleLevel < userRoleLevel && !navigation.some(item => item.name === items[0]?.name)) {
      navigation = [...navigation, ...items];
    }
  }

  return navigation;
}

export function getUserRoleLevel(userRole?: RoleName): number {
  return userRole ? ROLE_LEVELS[userRole] || 0 : 0;
}

export function canManageRole(managerRole: RoleName, targetRole: RoleName): boolean {
  const managerLevel = ROLE_LEVELS[managerRole] || 0;
  const targetLevel = ROLE_LEVELS[targetRole] || 0;
  return managerLevel > targetLevel;
}

// Permission checking functions for user objects
// These functions accept a user object (with optional role property) and return boolean

interface UserWithRole {
  role?: {
    name?: RoleName | string;
    id?: number;
  } | null;
  id?: number;
  email?: string;
  name?: string;
}

/**
 * Check if a user has a valid role loaded from backend
 */
export function hasValidUserRole(user: UserWithRole | null | undefined, isAuthenticated: boolean): boolean {
  if (!isAuthenticated || !user) return false;
  return !!user.role?.name;
}

/**
 * Check if user is an admin
 */
export function isUserAdmin(user: UserWithRole | null | undefined): boolean {
  return user?.role?.name === ROLE_NAMES.ADMIN;
}

/**
 * Check if user can manage streams (admin or media_team)
 */
export function canManageStreams(user: UserWithRole | null | undefined): boolean {
  if (!user?.role?.name) return false;
  return canAccessFeature('manage_streams', user.role.name as RoleName);
}

/**
 * Check if user can moderate content (admin, moderator, media_team, or pastor)
 */
export function canModerateContent(user: UserWithRole | null | undefined): boolean {
  if (!user?.role?.name) return false;
  return canAccessFeature('moderate_comments', user.role.name as RoleName);
}

/**
 * Check if user can manage users (admin only)
 */
export function canManageUsers(user: UserWithRole | null | undefined): boolean {
  if (!user?.role?.name) return false;
  return canAccessFeature('manage_users', user.role.name as RoleName);
}
